import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { TFormBuilderEntityDomNodeRefMap } from "./hooks/entityDomNodeRefMap/useFormBuilderEntityDomNodeRefMapRegistration";
import { FormBuilderEditorContext } from "./formBuilderEditorContext";
import { useWebsiteBuilderDndManager } from "pages/secure/facility/websiteBuilder/editor/hooks/dndManager/useWebsiteBuilderDndManager";
import { FormBuilderPanel } from "./panel/FormBuilderPanel";
import { isWebsiteBuilderTextEditableTarget } from "pages/secure/facility/websiteBuilder/editor/utility/websiteBuilderTextEditableTarget";
import { cloneDeep } from "lodash";
import { FormBuilderFormView } from "./designSpace/views/components/form/FormBuilderFormView";
import { formBuilderGenerateDefaultForm } from "./model/formBuilderForm";
import { useAppSelector } from "hooks/redux";
import { TFormBuilderSaveFormRef } from "../FormBuilder";
import { useWebsiteBuilderEditorContext } from "../../websiteBuilderEditorContext";
import { TFormBuilderItem } from "./model/item/formBuilderItem";
import { useFormBuilderController } from "./controller/useFormBuilderController";
import "./formBuilderEditor.scss";
import "./designSpace/styles/builder.scss";

interface IFormBuilderEditorProps {
  selectedFormId: number;
  saveFormRef: TFormBuilderSaveFormRef;
}

export function FormBuilderEditor(props: IFormBuilderEditorProps) {
  const { selectedFormId, saveFormRef } = props;

  const { websiteId } = useWebsiteBuilderEditorContext();
  
  const dndManager = useWebsiteBuilderDndManager();

  const forms = useAppSelector(store => store.websiteBuilderStore.editor.forms);

  const panelRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  
  const formBuilderEntityDomNodeRefMap: TFormBuilderEntityDomNodeRefMap = useRef(new Map());

  const initialForm = useMemo(() => {
    const form = forms.find(form => form.id === selectedFormId);

    if (form == null) {
      return formBuilderGenerateDefaultForm(websiteId);
    } else {
      return cloneDeep(form);
    }
  }, []);

  const formBuilderController = useFormBuilderController(initialForm);

  useEffect(() => {
    saveFormRef.current = formBuilderController.form;

    return () => saveFormRef.current = null;
  }, [formBuilderController.form]);

  const handleKeydown = useCallback(
    (e: KeyboardEvent) => {
      const modifierKeyPressed = e.ctrlKey || e.metaKey;

      if (modifierKeyPressed && e.key.toLowerCase() === "z" && !isWebsiteBuilderTextEditableTarget(e.target)) {
        e.preventDefault();
      }

      if (isWebsiteBuilderTextEditableTarget(e.target) || dndManager.isDragging || (formBuilderController.selectedEntity != null && formBuilderController.selectedEntity.editingMode.type !== "none")) {
        return;
      }

      if (modifierKeyPressed && e.key.toLowerCase() === "z") {
        if (e.shiftKey) {
          formBuilderController.redo();
        } else {
          formBuilderController.undo();
        }
      } else if (formBuilderController.selectedEntity != null) {
        if (e.key === "Escape") {
          if (formBuilderController.selectedEntity.type === "item") {
            formBuilderController.selectEntity({
              selectedEntity: {
                type: "row",
                key: formBuilderController.selectedEntity.rowKey,
                editingMode: {
                  type: "none",
                },
              },
            });
          } else {
            formBuilderController.selectEntity({ selectedEntity: null });
          }
        } else if (e.key === "Backspace") {
          const entity = formBuilderController.entityMap.get(formBuilderController.selectedEntity.key);

          if (entity == null) {
            return;
          }

          if (entity.type === "row" && formBuilderController.selectedEntity.type === "row") {
            const entityIndex = formBuilderController.form.children.findIndex(child => child.key === entity.key);

            if (entityIndex === -1) {
              return;
            }

            formBuilderController.deleteEntity({
              snapshot: {
                type: "row",
                entity : cloneDeep(entity),
                entityIndex,
              },
            });
          } else if (entity.type === "item" && formBuilderController.selectedEntity.type === "item") {
            const parentRow = formBuilderController.entityMap.get(formBuilderController.selectedEntity.rowKey);

            if (parentRow == null || parentRow.type !== "row") {
              return;
            }

            const parentRowIndex = formBuilderController.form.children.findIndex(child => child.key === parentRow.key);
            const entityIndex = parentRow.children.findIndex((child: TFormBuilderItem) => child.key === entity.key);

            if (parentRowIndex === -1 || entityIndex === -1) {
              return;
            }

            formBuilderController.deleteEntity({
              snapshot: {
                type: "item",
                origin: "DELETE_ENTITY",
                entity: cloneDeep(entity),
                entityIndex,
                parentRow: cloneDeep(parentRow),
                parentRowIndex,
              },
            });
          }
        }
      }
    },
    [
      dndManager.isDragging,
      formBuilderController,
    ],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeydown);

    return () => document.removeEventListener("keydown", handleKeydown);
  }, [handleKeydown]);

  const handlePointerDown = useCallback((e: PointerEvent) => {
    if (
      panelRef.current == null ||
      formRef.current == null ||
      !(e.target instanceof Node) ||
      panelRef.current.contains(e.target) ||
      formRef.current.contains(e.target)
    ) {
      return;
    }

    formBuilderController.selectEntity({ selectedEntity: null });
  }, [formBuilderController.selectEntity]);

  useEffect(() => {
    document.addEventListener("pointerdown", handlePointerDown, { capture: true });

    return () => document.removeEventListener("pointerdown", handlePointerDown, { capture: true });
  }, [handlePointerDown]);

  return (
    <div className="form-builder-editor">
      <FormBuilderEditorContext.Provider
        value={{
          panelRef,
          formBuilderController,
          formRef,
          formBuilderEntityDomNodeRefMap,
        }}
      >
        <FormBuilderPanel />

        <div className="form-builder-editor-design-space">
          <FormBuilderFormView />
        </div>
      </FormBuilderEditorContext.Provider>
    </div>
  );
}
